(ns ui.view.entity-graph.event
  (:require [reagent.core :as r]
            [re-frame.core :as rf]
            [clojure.set :as set]
            [clojure.string :as string]
            [common.entity :as entity]
            [common.localization :as loc]
            [common.schema.entities.graph-reference :as graph-reference]
            [common.schema.entity-types :as entity-types]
            [common.util :as cu]
            [potpuri.core :as potpuri]
            [ui.state.session.model :as session-model]
            [ui.view.entity-graph.auto-layout :as auto-layout]
            [ui.view.entity-graph.model :as m]
            [ui.view.entity-graph.export :as export]
            [frontend.controller.websocket.event :as ws]
            [frontend.local-storage :as ls]
            [frontend.state.current-session :as session]
            [frontend.state.permissions :as permissions]
            [frontend.view.modals.events :as modals]
            [frontend.view.modals.error-message.view :as error-message]
            [ui.view.entity-graph.utils :as utils]
            [frontend.subscription :as ws-sub]))

(rf/reg-event-fx
 m/create-connector
 []
 (fn [{:keys [db]} [_ {:keys [source-id target-id type] :as ctx}]]
   (let [{:keys [edits direction]} (-> db
                                       (get-in (m/create-connector-modal-full-path ctx)))
         map-profile-edits (-> db
                               (get-in (m/edit-map-profile-full-path ctx))
                               :edits)
         ;; direction defines where the creator was opened from => the map-profile
         ;; data will be the other one (source <=> target)
         map-profile-key (if (= direction :source)
                           :target-data
                           :source-data)
         allowed-meta-attributes (get graph-reference/graph-references-metadata type)
         metadatas (->> (select-keys edits allowed-meta-attributes)
                        (filterv (fn [[_a v]]
                                   (not (cu/empty-value? v))))
                        (mapv (fn [[a v]]
                                {a v})))
         system-config (get-in db (m/selected-system-config-full-path ctx))
         hidden-types (get-in m/system-level-configs
                              [system-config :hide-connector-types])
         ;; if system-level-config AND we're creating a connector that would be hidden
         ;; => after creation the new connector should be shown and system-level-config
         ;;    no longer applies
         reset-system-config? (contains? hidden-types type)
         new-db (assoc-in
                 db (m/creating-full-path ctx)
                 true)]
     {:db new-db
      ::ws/send {:id :graph-reference/create
                 :body (cond-> {:source source-id
                                :target target-id
                                :type type}
                         (seq metadatas) (assoc :metadata
                                                metadatas)
                         (seq map-profile-edits) (assoc map-profile-key
                                                        map-profile-edits))
                 ;; note: might need a success-event specific to connectors
                 :on-success [m/create-success (assoc ctx
                                                      :reset-system-config?
                                                      reset-system-config?)]
                 :on-error [m/create-connector-error ctx]}})))

(defn open-error-modal!
  [error-message]
 (modals/open-modal!
  error-message/modal-key
  {:title
   (loc/tr :attention)
   :body-content
   error-message
   :ok-button-text
   (loc/tr :ok)}))

(rf/reg-event-fx
 m/create-connector-error
 []
 (fn [{:keys [db]} [_ ctx error-response]]
   (let [{:keys [error message]} error-response
         msg (cond (string? message) message
                   (keyword? error) (loc/tr error)
                   :else (loc/tr :unknown-error-persists))
         new-db (assoc-in
                 db (m/error-full-path ctx)
                 "Error creating a connector")]
     {:db new-db
      :fx [[::modals/open-modal (partial open-error-modal! msg)]
           [:dispatch-later
            {:ms 100
             :dispatch ^:flush-dom [m/close-node-menu ctx]}]
           [:dispatch-later
            {:ms 100
             :dispatch ^:flush-dom [m/close-create-connector ctx]}]]})))

;; TODO remove when confirmed no longer needed:
#_(rf/reg-event-fx
 m/create-map-profile
 []
 (fn [{:keys [db]} [_ {:keys [entity-id] :as ctx}]]
   (let [new-db (assoc-in
                 db (m/creating-full-path ctx)
                 true)]
     {:db new-db
      ::ws/send {:id :graph-entity/create
                 :body {:entity-id entity-id}
                 ;; note: might need a success-event specific to map-map-profiles
                 :on-success [m/create-success ctx]
                 :on-error [m/create-map-profile-error ctx]}
      #_#_:fx [[:dispatch-later
            {:ms 500
             :dispatch [m/create-success ctx (fake-map-profile ctx)]}]]})))

(defn resolve-system-config-hidden-connector-ids
  [{:keys [db] :as cofx} ctx]
  (let [system-config (get-in db (m/selected-system-config-full-path ctx))
        hide-types (get-in m/system-level-configs
                           [system-config :hide-connector-types])
        reachable-connectors (utils/get-reachable-connectors cofx ctx)
        to-hide (->> reachable-connectors
                     (into
                      #{}
                      (comp
                       (filter (fn [{:keys [graph-reference/type]}]
                                 (contains? hide-types type)))
                       (map :db/id))))]
    to-hide))

(rf/reg-event-fx
 m/create-success
 []
 (fn [{:keys [db] :as cofx} [_ ctx {:keys [body] :as _result} :as _args]]
   (let [{:keys [entities]} body
         system-config (get-in db (m/selected-system-config-full-path ctx))
         reset-system-config? (and (:reset-system-config? ctx)
                                   system-config)
         eids-to-remove (->> entities
                             (filterv :common/disabled?)
                             (mapv :db/id))
         current-hidden (when reset-system-config?
                          (resolve-system-config-hidden-connector-ids cofx ctx))
         entities-by-id (potpuri/index-by :db/id entities)
         db (if-not reset-system-config?
              db
              (-> db
                  (update-in (m/hide-rules-full-path ctx)
                             assoc :connectors current-hidden)
                  (potpuri/dissoc-in (m/selected-system-config-full-path ctx))))
         new-db (-> db
                    (potpuri/dissoc-in (m/creating-full-path ctx))
                    (update-in session-model/entities-path
                               merge entities-by-id)
                    (update-in session-model/entities-path
                               (partial apply dissoc) eids-to-remove))]

     {:db new-db
      :fx [[:dispatch ^:flush-dom [m/close-node-menu ctx]]
           [:dispatch ^:flush-dom [m/close-create-connector ctx]]]})))

#_(rf/reg-event-fx
 m/create-map-profile-error
 []
 (fn [{:keys [db]} [_ ctx {:keys [error message] :as _result}]]
   (let [_ (prn ">error:" error message)
         new-db (-> db
                    (potpuri/dissoc-in (m/creating-full-path ctx))
                    ;;TODO display-error
                    )]

     {:db new-db
      :fx [[:dispatch-later
            {:ms 10
             :dispatch ^:flush-dom [m/close-node-menu ctx]}]
           [:dispatch-later
            {:ms 10
             :dispatch ^:flush-dom [m/close-create-connector ctx]}]]})))

(rf/reg-event-fx
 m/ws-sub-error
 []
 (fn [{:keys [db]} [_ {:keys [_main-entity-id] :as ctx} error]]
   (let [msg (if (keyword error)
               (loc/tr error)
               (loc/tr :unknown-error-persists))
         new-db (-> db
                    (assoc-in (m/error-full-path ctx) {:error "error"})
                    (assoc-in (m/loaded-full-path ctx) true))]
     {:db new-db
      :fx [[::modals/open-modal (partial open-error-modal! msg)]]})))

(defn ws-sub-update
  [{:keys [db]} [_ {:keys [_main-entity-id] :as ctx} {:keys [entities
                                                             synthetic-timestamp]
                                                      :as _result}]]
  (let [;;_ (prn ">ws-sub-update" _result)
        eids-to-remove (->> entities
                            (filterv :common/disabled?)
                            (mapv :db/id))
        entities-by-id (potpuri/index-by :db/id entities)
        new-db (-> db
                   (potpuri/dissoc-in (m/creating-full-path ctx))
                   (assoc-in (m/loaded-full-path ctx) true)
                   (update-in session-model/entities-path
                              merge entities-by-id)
                   (update-in session-model/entities-path
                              (partial apply dissoc) eids-to-remove))
        new-db (cond-> new-db
                 (number? synthetic-timestamp)
                 (assoc-in (m/synthetic-timestamp-full-path ctx) synthetic-timestamp))]
    {:db new-db}))

(rf/reg-event-fx
 m/ws-sub-update
 []
 ws-sub-update)

(rf/reg-event-fx
 m/fetch-initial-data-success
 []
 (fn [{:keys [db]} [_ {:keys [main-entity-id] :as ctx} {:keys [body] :as _result}]]
   (let [{:keys [entities
                 synthetic-timestamp]} body
         ;;_ (prn ">initial-data-success" synthetic-timestamp body)
         eids-to-remove (->> entities
                             (filterv :common/disabled?)
                             (mapv :db/id))
         main-graph-entity-id (->> entities
                                   (filter (fn [{:keys [graph-entity/entity]}]
                                             (= entity main-entity-id)))
                                   first
                                   :db/id)
         main-graph-entity-id (if main-graph-entity-id
                                main-graph-entity-id
                                (:db/id
                                 (utils/find-main-map-profile
                                  {:entities (-> db
                                                (get-in session-model/entities-path)
                                                vals)
                                   :main-entity-id main-entity-id})))
         initialized? (-> db
                          (get-in (m/ready-full-path ctx)))
         entities-by-id (potpuri/index-by :db/id entities)
         new-db (-> db
                    (potpuri/dissoc-in (m/creating-full-path ctx))
                    (assoc-in (m/loaded-full-path ctx) true)
                    (update-in session-model/entities-path
                               merge entities-by-id)
                    (update-in session-model/entities-path
                               (partial apply dissoc) eids-to-remove))
         new-db (cond-> new-db
                  (number? synthetic-timestamp)
                  (assoc-in (m/synthetic-timestamp-full-path ctx) synthetic-timestamp))]
     {:db new-db
      :fx [(when initialized?
             [:dispatch [m/loaded-and-initialized ctx]])
           [::ws-sub/open
            {:key (m/ws-sub-key ctx)
             :type :entity-graph
             :opts {:entity-id main-entity-id
                    :graph-entity-id main-graph-entity-id
                    :synthetic-timestamp synthetic-timestamp}
             :on-update-event [m/ws-sub-update ctx]
             :on-error-event [m/ws-sub-error ctx]
             :close-previous-subscription-if-exists? true}]]})))

(defn ws-sub-close
  [{:keys [db]} [_ {:keys [_main-entity-id] :as ctx}]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/i-root ctx)))]
    ;; TODO different synthetic-timestamp for entity-id specific view-configs?
    {:db new-db}))

(rf/reg-event-fx
 m/ws-sub-close
 []
 ws-sub-close)

(defn loaded-and-initialized
  [{:keys [db local-storage]} [_ {:keys [_main-entity-id] :as ctx}]]
  (let [system-config (get local-storage :system-config)
        config (get local-storage :config)]
    (cond-> {:db db}
      (and config (not system-config))
      (assoc :fx [[:dispatch [m/load-view-config
                              (assoc ctx :config config)]]])
      (and (not config)
           (not system-config))
      (assoc :fx [[:dispatch [m/auto-layout-nodes ctx]]])

      system-config
      (assoc :fx [[:dispatch [m/select-system-config
                              (assoc ctx
                                     :config
                                     {:db/id system-config})]]]))))

(defn ls-path
  [{:keys [main-entity-id] :as _ctx}]
  [:eg @session/tenant-id @session/account-id main-entity-id])

(defn local-storage-cofx
  [{:keys [original-event] :as cofx}]
  (let [[_ ctx] original-event
        ls-data (ls/get-in (ls-path ctx))]
    (assoc cofx :local-storage ls-data)))

(rf/reg-cofx
 m/local-storage-cofx
 local-storage-cofx)

(rf/reg-event-fx
 m/loaded-and-initialized
 [(rf/inject-cofx m/local-storage-cofx)]
 loaded-and-initialized)

(defn cleanup
  [{:keys [db]} [_ {:keys [_main-entity-id] :as ctx}]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/i-root ctx)))]
    {:db new-db
     :fx [[m/to-local-storage-fx {:db db :ctx ctx}]
          [::ws-sub/close
           {:key (m/ws-sub-key ctx)
            :callback-event [m/ws-sub-close ctx]}]]}))

(rf/reg-event-fx
 m/cleanup
 []
 cleanup)

(rf/reg-event-fx
 m/init-state
 []
 (fn [{:keys [db]} [_ {:keys [main-entity-id] :as ctx}]]
   (let [new-db (potpuri/dissoc-in
                 db (m/ready-full-path ctx))
         ts (-> db
                (get-in (m/synthetic-timestamp-full-path ctx)))]
     {:db new-db
      :fx [[:fetch
            {:uri        "/entity-graph/query"
             :method     :post
             :body       (cond-> {:id     :initial-data
                                  :body {:entity-id main-entity-id}}
                           (number? ts) (assoc :synthetic-timestamp ts))
             :signal     (:signal ctx)
             :on-success [m/fetch-initial-data-success ctx]
             :on-failure [m/fetch-initial-data-error ctx]}]]})))

(rf/reg-event-fx
 m/fetch-initial-data-error
 []
 (fn [{:keys [db]} [_ ctx {:keys [body] :as _error-response}]]
   (let [{:keys [error message]} body
         msg (cond (string? message) message
                   (keyword? error) (loc/tr error)
                   :else (loc/tr :unknown-error-persists))

         new-db (-> db
                    (potpuri/dissoc-in (m/creating-full-path ctx)))]
     ;;(prn ">error" body)
     {:db new-db
      :fx [[::modals/open-modal (partial open-error-modal! msg)]]})))

(rf/reg-event-fx
 m/flow-initialized
 []
 (fn [{:keys [db]} [_ {:keys [provider] :as ctx}]]
   (let [new-db (-> db
                    (assoc-in (m/ready-full-path ctx) true)
                    (assoc-in (m/provider-full-path ctx) provider))
         loaded? (-> db
                     (get-in (m/loaded-full-path ctx)))]
     (cond-> {:db new-db}
       loaded?
       (assoc :fx [[:dispatch [m/loaded-and-initialized ctx]]])))))

(defn update-selects
  [selected select unselect]
  (cond-> (or selected #{})
    (seq select) (set/union select)
    (seq unselect) (set/difference unselect)))

(rf/reg-event-fx
 m/select-edges
 []
 (fn [{:keys [db]} [_ ctx {:keys [select unselect]}]]
   (let [selected (get-in db (m/selected-edges-full-path ctx))
         ;; new selected => open sidebar
         new-selected-edges? (seq (set/difference select
                                                  selected))
         sidebar-mode (get-in db (m/sidebar-mode-full-path ctx))
         sidebar-pinned? (get-in db (m/sidebar-pinned-full-path ctx))
         open-sidebar? (and (not= sidebar-mode :expanded)
                            (not sidebar-pinned?)
                            new-selected-edges?)
         new-db (-> db
                    (update-in (m/selected-edges-full-path ctx)
                               update-selects select unselect))]
     (cond-> {:db new-db}
       open-sidebar? (assoc :fx
                            [[:dispatch [m/set-sidebar-mode
                                         (assoc ctx
                                                :sidebar-mode :expanded)]]])))))

(rf/reg-event-fx
 m/node-changes
 []
 (fn [{:keys [db] :as cofx} [_ ctx {:keys [ephemeral
                                           view-config
                                           selects
                                           auto-layout?] :as _args}]]
   (let [{:keys [positions remove-positions]} ephemeral
         {:keys [select unselect]} selects
         {view-positions :positions} view-config
         selected (get-in db (m/selected-nodes-full-path ctx))
         ;; new selected => open sidebar
         new-selected-nodes? (seq (set/difference select
                                                  selected))
         sidebar-mode (get-in db (m/sidebar-mode-full-path ctx))
         sidebar-pinned? (get-in db (m/sidebar-pinned-full-path ctx))
         open-sidebar? (and (not= sidebar-mode :expanded)
                            (not sidebar-pinned?)
                            new-selected-nodes?)
         manual-positions? (get-in db (m/manual-positions-full-path ctx))
         set-manual-positions? (and (not manual-positions?)
                                    (seq view-positions)
                                    (not auto-layout?))
         system-config (get-in db (m/selected-system-config-full-path ctx))
         reset-system-config? (and system-config
                                   ;; change coming from auto-layout
                                   ;; => don't reset system-config:
                                   (not auto-layout?)
                                   (or (seq view-positions)
                                       set-manual-positions?))
         hidden-connectors (when reset-system-config?
                             (resolve-system-config-hidden-connector-ids cofx ctx))
         new-db (cond-> db
                  (seq selects)
                  (update-in (m/selected-nodes-full-path ctx)
                             update-selects select unselect)

                  (seq view-positions)
                  (update-in (m/view-positions-full-path ctx)
                             merge view-positions)

                  set-manual-positions?
                  (update-in (m/view-config-full-path ctx)
                             assoc m/manual-positions-key true)

                  reset-system-config?
                  (potpuri/dissoc-in (m/selected-system-config-full-path ctx))

                  reset-system-config?
                  (update-in (m/hide-rules-full-path ctx)
                             assoc :connectors hidden-connectors)

                  (seq remove-positions)
                  (update-in (m/ephemeral-positions-full-path ctx)
                             (partial apply dissoc) remove-positions)

                  (seq positions)
                  (update-in (m/ephemeral-positions-full-path ctx)
                             merge positions))]
     (cond-> {:db new-db}
       open-sidebar? (assoc :fx
                            [[:dispatch [m/set-sidebar-mode
                                         (assoc ctx
                                                :sidebar-mode :expanded)]]])))))

(rf/reg-event-fx
 m/open-settings
 []
 (fn [{:keys [db]} [_ ctx]]
   (let [new-db (-> db
                    (update-in (m/settings-full-path ctx)
                               assoc :open? true))]
     {:db new-db})))

(defn close-settings
  [{:keys [db]} [_ ctx]]
  (let [new-db (-> db
                   ;;dissoc full settings-state(?)
                   (update-in (m/settings-full-path ctx)
                              dissoc :open?))]
    {:db new-db}))

(rf/reg-event-fx
 m/close-settings
 []
 close-settings)

(rf/reg-event-fx
 m/open-hiding-view
 []
 (fn [{:keys [db]} [_ {:keys [map-profile-id] :as ctx}]]
   (let [hide-rules (get-in db (m/hide-rules-full-path ctx))
         new-db (-> db
                    (update-in (m/hiding-view-full-path ctx)
                               assoc map-profile-id {:open? true})
                    (assoc-in (m/ephemeral-hide-rules-full-path ctx)
                              hide-rules))]
     {:db new-db})))

(defn discard-ephemeral-hide-rules
  [db ctx]
  (let [new-db (-> db
                   (assoc-in (m/ephemeral-hide-rules-full-path ctx)
                             nil))]
    new-db))

(defn close-hiding-view
  [{:keys [db]} [_ {:keys [map-profile-id] :as ctx}]]
  (let [new-db (-> db
                   (discard-ephemeral-hide-rules ctx)
                   (update-in (m/hiding-view-full-path ctx)
                              dissoc map-profile-id))]
    {:db new-db}))

(rf/reg-event-fx
 m/close-hiding-view
 []
 close-hiding-view)

(defn set-sidebar-mode
  [{:keys [db] :as cofx} [_ {:keys [sidebar-mode] :as ctx}]]
  (let [pinned? (= sidebar-mode :pinned)
        currently-pinned? (get-in db (m/sidebar-pinned-full-path ctx))
        save-to-local-storage? (or pinned?
                                   (and (not pinned?)
                                        currently-pinned?))
        system-config (get-in db (m/selected-system-config-full-path ctx))
        reset-system-config? (and save-to-local-storage?
                                  system-config)
        hidden-connectors (when reset-system-config?
                            (resolve-system-config-hidden-connector-ids cofx ctx))
        new-db (cond-> db
                 true (assoc-in (m/sidebar-mode-full-path ctx)
                                sidebar-mode)
                 pinned? (assoc-in (m/sidebar-pinned-full-path ctx)
                                   true)
                 (not pinned?) (potpuri/dissoc-in (m/sidebar-pinned-full-path ctx))
                 reset-system-config? (update-in (m/hide-rules-full-path ctx)
                                                 assoc :connectors hidden-connectors)
                 reset-system-config? (potpuri/dissoc-in
                                       (m/selected-system-config-full-path ctx)))]
    (cond-> {:db new-db}
      save-to-local-storage?
      (assoc :fx [[m/to-local-storage-fx {:db new-db
                                          :ctx ctx}]]))))

(rf/reg-event-fx
 m/set-sidebar-mode
 []
 set-sidebar-mode)

(defn open-edit-map-profile
  [{:keys [db]} [_ {:keys [map-profile-id] :as ctx}]]
  (let [new-db (-> db
                   (update-in (m/edit-map-profile-full-path ctx)
                              assoc
                              :open? true
                              :map-profile-id map-profile-id))]
    {:db new-db}))

(rf/reg-event-fx
 m/open-edit-map-profile
 []
 open-edit-map-profile)

(defn open-edit-connector
  [{:keys [db]} [_ {:keys [connector-id
                           other-mp-id] :as ctx}]]
  (let [new-db (-> db
                   (update-in (m/edit-connector-full-path ctx)
                              assoc
                              :open? true
                              :connector-id connector-id
                              :other-mp-id other-mp-id))]
    {:db new-db}))

(rf/reg-event-fx
 m/open-edit-connector
 []
 open-edit-connector)

(defn preview-hide-connector
  [{:keys [db]} [_ {:keys [connector-id hide?] :as ctx}]]
  (let [new-db (-> db
                   (update-in (m/ephemeral-hide-rules-full-path ctx)
                              (fn [hide-rules]
                                (let [action (if hide?
                                               (fnil conj #{})
                                               (fnil disj #{}))]
                                  (update hide-rules
                                          :connectors
                                          action connector-id)))))]
    {:db new-db}))

(rf/reg-event-fx
 m/preview-hide-connector
 []
 preview-hide-connector)

;; selected-system-config makes this a bit more complex:
;; -resolve all hidden edges (i.e. :ownerships => all reachable non-ownerships etc)
;; -hide/unhide given connector-id based on that set
(defn hide-connector
  [{:keys [db] :as cofx} [_ {:keys [connector-id hide?] :as ctx}]]
  (let [system-config (get-in db (m/selected-system-config-full-path ctx))
        hidden-connectors (if-not system-config
                            (get-in db (conj (m/hide-rules-full-path ctx)
                                             :connectors))
                            (resolve-system-config-hidden-connector-ids cofx ctx))
        action (if hide?
                 (fnil conj #{})
                 (fnil disj #{}))
        new-hidden-connectors (action hidden-connectors connector-id)
        ;; visible-connectors => figure out hidden
        new-db (-> db
                   (update-in (m/hide-rules-full-path ctx)
                              assoc :connectors new-hidden-connectors)
                   ;; possible system-level-config no longer applies:
                   (potpuri/dissoc-in (m/selected-system-config-full-path ctx)))]
    {:db new-db}))

(rf/reg-event-fx
 m/hide-connector
 []
 hide-connector)

(defn connectors-of-type
  [db connector-type]
  (let [entities (-> db
                     (get-in session-model/entities-path)
                     vals)
        connectors (->> entities
                        (filterv (fn [{:keys [entity/type]}]
                                   (= type :entity.type/graph-reference))))]
    (->> connectors
         (filterv (fn [{:keys [graph-reference/type]}]
                    (= type connector-type))))))

;; note: not used anymore - remove when confirmed that not needed
(defn preview-hide-all
  [{:keys [db]} [_ {:keys [connector-ids connector-type hiding? val] :as ctx}]]
  (let [hide-rules-connectors (-> (get-in db (m/hide-rules-full-path ctx))
                                  :connectors)
        connectors-of-type (connectors-of-type db connector-type)
        change (case [(boolean hiding?) (boolean val)]
                 [true true] :hide-connectors
                 [true false] :undo-hide-connectors
                 [false true] :unhide-connectors
                 [false false] :undo-unhide-connectors)
        connector-ids (if hiding?
                        ;; only visible connector-ids:
                        connector-ids
                        ;; all connector-ids of type:
                        (->> connectors-of-type
                             (into #{}
                                   (map :db/id))))
        new-db (-> db
                   (update-in (m/ephemeral-hide-rules-full-path ctx)
                              (fn [hide-rules]
                                (let [ephemeral-connectors (:connectors hide-rules)
                                      ;; somewhat (inherently) complex logic here:
                                      new-connectors
                                      (case change
                                        :hide-connectors
                                        (set/union
                                         hide-rules-connectors
                                         ephemeral-connectors
                                         connector-ids)

                                        :undo-hide-connectors
                                        (set/difference
                                         (set/union
                                          hide-rules-connectors
                                          ephemeral-connectors)
                                         connector-ids)

                                        :unhide-connectors
                                        (set/difference
                                         (set/union
                                          ;; if ephemeral-connectors is not nil
                                          ephemeral-connectors
                                          (when (nil? ephemeral-connectors)
                                            hide-rules-connectors)
                                          ephemeral-connectors)
                                         connector-ids)

                                        :undo-unhide-connectors
                                        (set/union
                                         (set/difference
                                          ephemeral-connectors
                                          connector-ids)
                                         (set/intersection
                                          connector-ids
                                          hide-rules-connectors)))]
                                  (assoc hide-rules
                                         :connectors
                                         new-connectors)))))]
    {:db new-db}))

(defn update-system-config-hidden
  [{:keys [current-hidden show? connector-type connectors]}]
  (let [matching-connector-ids (->> connectors
                                    (filterv (fn [{:keys [graph-reference/type]}]
                                               (= type connector-type)))
                                    (mapv :db/id)
                                    (into #{}))
        action (if show?
                 ;; remove matching connectors-ids from currently hidden connectors
                 set/difference
                 ;; or combine them when hiding all:
                 set/union)]
    (action current-hidden matching-connector-ids)))

;; hide/unhide all given connectors
;;  OR if system-level-config: first resolve hidden connector-ids
;;  (i.e. all reachable connectors of system-config's hidden types)
;;  then update it based on hide/show all type
(defn hide-all
  [{:keys [db] :as cofx} [_ {:keys [connector-ids
                                    connector-type
                                    show?] :as ctx}]]
  (let [system-config (get-in db (m/selected-system-config-full-path ctx))
        current-hidden (if system-config
                         (resolve-system-config-hidden-connector-ids cofx ctx)
                         (get-in db (conj (m/hide-rules-full-path ctx)
                                          :connectors)))
        new-hidden (if system-config
                     (update-system-config-hidden
                      {:current-hidden current-hidden
                       :show? show?
                       :connector-type connector-type
                       :connectors (utils/get-reachable-connectors cofx ctx)})
                     (if show?
                       (set/difference current-hidden connector-ids)
                       (set/union current-hidden connector-ids)))
        new-db (cond-> db
                 true (update-in (m/hide-rules-full-path ctx)
                                 assoc :connectors
                                 new-hidden)
                 true (potpuri/dissoc-in (m/selected-system-config-full-path ctx))
                 system-config (update (m/hide-rules-full-path ctx)
                                       dissoc :system-config :connector-types))]
    {:db new-db}))

;;TODO rename into preview-hide-all-connectors if necessary to hide also
;; map-profiles
(rf/reg-event-fx
 m/preview-hide-all
 []
 preview-hide-all)

(rf/reg-event-fx
 m/hide-all
 []
 hide-all)

(defn open-map-profile-curtain
  [{:keys [db]} [_ {:keys [map-profile-id] :as ctx}]]
  (let [new-db (-> db
                   ;; ensure no hidden nodes stay as selected
                   (assoc-in (m/selected-nodes-full-path ctx)
                             #{map-profile-id})
                   (discard-ephemeral-hide-rules ctx))]
    {:db new-db}))

(rf/reg-event-fx
 m/open-map-profile-curtain
 []
 open-map-profile-curtain)

;;all necessary cleanup done in open-map-profile-curtain(?)
(defn close-map-profile-curtain
  [{:keys [db]} [_ {:keys [map-profile-id] :as ctx}]]
  (let [new-db (-> db
                   (update-in (m/hiding-view-full-path ctx)
                              dissoc map-profile-id)
                   (discard-ephemeral-hide-rules ctx))]
    {:db new-db}))

(rf/reg-event-fx
 m/close-map-profile-curtain
 []
 close-map-profile-curtain)

(defn save-map-profile
  [{:keys [db]} [_ {:keys [map-profile-id] :as ctx}]]
  (let [edits (-> db
                  (get-in (m/edit-map-profile-full-path ctx))
                  :edits)
        map-profile (-> db
                        (get-in session-model/entities-path)
                        (get map-profile-id))
        changes (->> edits
                     (reduce-kv (fn [m k v]
                                  (if (not= v (get map-profile k))
                                    (assoc m k v)
                                    m))
                                {}))
        changes? (seq changes)
        ;; spinner?
        new-db (-> db
                   (potpuri/dissoc-in (m/edit-map-profile-full-path ctx)))]
    (cond-> {:db new-db}
      changes?
      (assoc ::ws/send
             {:id :entity/update-attributes
              :body {:db/id map-profile-id
                     :data changes}
              :on-success [m/save-map-profile-success ctx]
              :on-error [m/save-map-profile-error ctx]}))))

(rf/reg-event-fx
 m/save-map-profile
 []
 save-map-profile)

(defn save-map-profile-success
  [{:keys [db]} [_ {:keys [_map-profile-id] :as ctx} {:keys [body]}]]
  (let [{:keys [entities]} body
        ;; note: :entity/update-attributes doesn't return :entities
        ;; leaving these for future:
        eids-to-remove (->> entities
                            (filterv :common/disabled?)
                            (mapv :db/id))
        entities-by-id (potpuri/index-by :db/id entities)
        new-db (-> db
                   (update-in session-model/entities-path
                              merge entities-by-id)
                   (update-in session-model/entities-path
                              (partial apply dissoc) eids-to-remove)
                   (potpuri/dissoc-in (m/edit-map-profile-full-path ctx)))]
    {:db new-db}))

(rf/reg-event-fx
 m/save-map-profile-success
 []
 save-map-profile-success)

(defn save-map-profile-error
  [{:keys [db]} [_ ctx error-response]]
  (let [{:keys [error message]} error-response
        msg (cond (string? message) message
                  (keyword? error) (loc/tr error)
                  :else (loc/tr :unknown-error-persists))
        new-db (assoc-in
                db (m/error-full-path ctx)
                "Error updating a map-profile")]
    {:db new-db
     :fx [[::modals/open-modal (partial open-error-modal! msg)]
          [:dispatch-later
           {:ms 100
            :dispatch ^:flush-dom [m/close-node-menu ctx]}]
          [:dispatch-later
           {:ms 100
            :dispatch ^:flush-dom [m/close-create-connector ctx]}]]}))

(rf/reg-event-fx
 m/save-map-profile-error
 []
 save-map-profile-error)

(defn discard-map-profile
  [{:keys [db]} [_ {:keys [_map-profile-id] :as ctx}]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/edit-map-profile-full-path ctx)))]
    {:db new-db}))

(rf/reg-event-fx
 m/discard-map-profile
 []
 discard-map-profile)

(defn save-connector
  [{:keys [db]} [_ {:keys [connector-id] :as ctx}]]
  (let [edits (-> db
                  (get-in (m/edit-connector-full-path ctx))
                  :edits)
        connector (-> db
                      (get-in session-model/entities-path)
                      (get connector-id))
        changes (->> edits
                     (reduce-kv (fn [m k v]
                                  (if (not= v (utils/metadata-value connector k))
                                    (assoc m k v)
                                    m))
                                {}))
        changes? (seq changes)
        new-db (-> db
                   (potpuri/dissoc-in (m/edit-connector-full-path ctx)))]
    (cond-> {:db new-db}
      changes?
      (assoc ::ws/send
             {:id :graph-reference/update-meta
              :body {:db/id connector-id
                     :data changes}
              :on-success [m/save-connector-success ctx]
              :on-error [m/save-connector-error ctx]}))))

(rf/reg-event-fx
 m/save-connector
 []
 save-connector)

(defn save-connector-error
  [{:keys [db]} [_ ctx error-response]]
  (let [{:keys [error message]} error-response
        msg (cond (string? message) message
                  (keyword? error) (loc/tr error)
                  :else (loc/tr :unknown-error-persists))
        new-db (assoc-in
                db (m/error-full-path ctx)
                "Error updating a graph-reference")]
    {:db new-db
     :fx [[::modals/open-modal (partial open-error-modal! msg)]
          [:dispatch-later
           {:ms 100
            :dispatch ^:flush-dom [m/close-node-menu ctx]}]
          [:dispatch-later
           {:ms 100
            :dispatch ^:flush-dom [m/close-create-connector ctx]}]]}))

(rf/reg-event-fx
 m/save-connector-error
 []
 save-connector-error)

(defn save-connector-success
  [{:keys [db]} [_ {:keys [_connector-id] :as ctx} {:keys [body]}]]
  (let [{:keys [entities]} body
        eids-to-remove (->> entities
                            (filterv :common/disabled?)
                            (mapv :db/id))
        entities-by-id (potpuri/index-by :db/id entities)
        new-db (-> db
                   (update-in session-model/entities-path
                              merge entities-by-id)
                   (update-in session-model/entities-path
                              (partial apply dissoc) eids-to-remove)
                   (potpuri/dissoc-in (m/edit-connector-full-path ctx)))]
    {:db new-db}))

(rf/reg-event-fx
 m/save-connector-success
 []
 save-connector-success)

(defn discard-connector
  [{:keys [db]} [_ {:keys [_connector-id] :as ctx}]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/edit-connector-full-path ctx))
                   (potpuri/dissoc-in (m/edit-map-profile-full-path ctx)))]
    {:db new-db}))

(rf/reg-event-fx
 m/discard-connector
 []
 discard-connector)

(defn edit-map-profile
  [{:keys [db]} [_ {:keys [_map-profile-id attribute value] :as ctx}]]
  (let [new-db (-> db
                   (update-in (m/edit-map-profile-full-path ctx)
                              update :edits
                              assoc attribute value))]
    {:db new-db}))

(rf/reg-event-fx
 m/edit-map-profile
 []
 edit-map-profile)

(defn prefill-map-person
  [_ctx entity]
  (let [attribute-mapping {:person/first-name :graph-entity/first-name
                           :person/last-name :graph-entity/last-name}
        attributes (->> attribute-mapping
                        (filterv (fn [[_e-attribute g-attribute]]
                                   (permissions/field-write-access?
                                    g-attribute :entity.type/graph-person nil)))
                        (mapv first))
        vals (select-keys entity attributes)
        vals (set/rename-keys vals attribute-mapping)]
    (cu/remove-blank-vals vals)))

;; map-profile attributes from target-entity
(defn prefill-map-profile
  [ctx entity]
  (let [entity-name (entity/display-string entity)
        entity-type (:entity/type entity)
        vals (case entity-type
               :entity.type/person
               (prefill-map-person ctx entity)
               {})]
    (merge
     {:graph-entity/label entity-name}
     vals)))

(defn start-edit-map-profile
  [{:keys [db session-entities]} [_ {:keys [target-entity-id] :as ctx}]]
  (let [entities (get-in db session-model/entities-path)
        target-entity (or (get entities target-entity-id)
                          (get session-entities target-entity-id))
        target-entity-type (:entity/type target-entity)
        main-entity? (contains? entity-types/graph-for-entity-types target-entity-type)
        ;; if graph-entity => dissoc edits - values got from existing map-profile,
        ;; if normal-entity => set label start-value as entity-name
        new-db (if main-entity?
                 (-> db
                     (update-in (m/edit-map-profile-full-path ctx)
                                assoc
                                :edits (prefill-map-profile ctx target-entity)
                                :new-map-profile? true))
                 (-> db
                     (potpuri/dissoc-in (m/edit-map-profile-full-path ctx))))]
    {:db new-db}))

(rf/reg-event-fx
 m/start-edit-map-profile
 [(rf/inject-cofx m/session-entities-cofx)]
 start-edit-map-profile)

(defn edit-connector
  [{:keys [db]} [_ {:keys [_connector-id attribute value] :as ctx}]]
  (let [new-db (-> db
                   (update-in (m/edit-connector-full-path ctx)
                              update :edits
                              assoc attribute value))]
    {:db new-db}))

(rf/reg-event-fx
 m/edit-connector
 []
 edit-connector)

(defn discard-hide-rules
  [{:keys [db]} [_ {:keys [map-profile-id] :as ctx}]]
  (let [new-db (cond-> db
                 true
                 (discard-ephemeral-hide-rules ctx)

                 map-profile-id
                 (update-in (m/hiding-view-full-path ctx)
                            dissoc map-profile-id))]
    {:db new-db}))

(rf/reg-event-fx
 m/discard-hide-rules
 []
 discard-hide-rules)

(defn apply-hide-rules
  [{:keys [db]} [_ {:keys [map-profile-id _global-settings?] :as ctx}]]
  (let [ephemeral-hide-rules (get-in db (m/ephemeral-hide-rules-full-path ctx))
        new-db (cond-> (-> db
                           (discard-ephemeral-hide-rules ctx)
                           (assoc-in (m/hide-rules-full-path ctx) ephemeral-hide-rules))
                 map-profile-id
                 (update-in (m/hiding-view-full-path ctx)
                            dissoc map-profile-id)

                 #_global-settings?)]
    {:db new-db}))

(rf/reg-event-fx
 m/apply-hide-rules
 []
 apply-hide-rules)

(defn update-defaults
  [{:keys [db] :as cofx} [_ {:keys [path value] :as ctx}]]
  (let [new-db (if (nil? value)
                 (-> db
                     (update-in (m/defaults-full-path ctx)
                                potpuri/dissoc-in path))
                 (-> db
                     (update-in (m/defaults-full-path ctx)
                                assoc-in path value)))
        reset-system-config? (get-in db (m/selected-system-config-full-path ctx))
        hidden-connectors (when reset-system-config?
                            (resolve-system-config-hidden-connector-ids cofx ctx))
        new-db (if reset-system-config?
                 (-> new-db
                     (potpuri/dissoc-in (m/selected-system-config-full-path ctx))
                     (update-in (m/hide-rules-full-path ctx)
                                assoc :connectors hidden-connectors))
                 new-db)]
    {:db new-db}))

(rf/reg-event-fx
 m/update-defaults
 []
 update-defaults)

(defn update-view-config
  [{:keys [db] :as cofx} [_ {:keys [path value] :as ctx}]]
  (let [new-db (if (nil? value)
                 (-> db
                     (update-in (m/view-config-full-path ctx)
                                potpuri/dissoc-in path))
                 (-> db
                     (update-in (m/view-config-full-path ctx)
                                assoc-in path value)))
        reset-system-config? (get-in db (m/selected-system-config-full-path ctx))
        hidden-connectors (when reset-system-config?
                            (resolve-system-config-hidden-connector-ids cofx ctx))
        new-db (if reset-system-config?
                 (-> new-db
                     (potpuri/dissoc-in (m/selected-system-config-full-path ctx))
                     (update-in (m/hide-rules-full-path ctx)
                                assoc :connectors hidden-connectors))
                 new-db)]
    {:db new-db}))

(rf/reg-event-fx
 m/update-view-config
 []
 update-view-config)

(defn auto-layout-nodes
  [{:keys [db] :as cofx} [_ ctx]]
  ;; note: ctx should contain :map-profiles and :connectors
  (let [all-visible-nodes? (not (contains? ctx :map-profiles))
        visible (when all-visible-nodes?
                  (utils/visible-nodes-edges cofx ctx))
        layout-ctx (if all-visible-nodes?
                     (assoc ctx
                            :map-profiles (:visible-map-profiles visible)
                            :connectors (:visible-connectors visible))
                     ctx)
        auto-positions (auto-layout/dagre-layout layout-ctx)
        provider (-> db
                     (get-in (m/provider-full-path ctx)))
        new-db (-> db
                   (update-in (m/view-positions-full-path ctx)
                              merge auto-positions))]
    (cond-> {:db new-db}
      all-visible-nodes?
      (assoc :fx [[m/fit-view-fx (assoc ctx :provider provider)]]))))

(rf/reg-event-fx
 m/auto-layout-nodes
 []
 auto-layout-nodes)

(defn discard-positions
  [{:keys [db]} [_ {:keys [auto-layout?] :as ctx}]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/view-positions-full-path ctx))
                   (potpuri/dissoc-in (m/ephemeral-positions-full-path ctx))
                   (potpuri/dissoc-in (m/manual-positions-full-path ctx)))]
    (cond-> {:db new-db}
      auto-layout?
      (assoc :fx [[:dispatch [m/auto-layout-nodes ctx]]]))))

(rf/reg-event-fx
 m/discard-positions
 []
 discard-positions)

(rf/reg-fx
 m/auto-layout-watch
 (fn [ctx]
   (r/after-render
    (partial auto-layout/add-auto-layout-watcher!
             ;; ensure ctx contains nothing extra
             (select-keys ctx
                          [:main-entity-id])))))

(rf/reg-fx
 m/remove-auto-layout-watch
 (fn [ctx]
   (auto-layout/remove-auto-layout-watcher!
    (select-keys ctx [:main-entity-id]))))

(defn to-local-storage
  [{:keys [db ctx]}]
  (let [config (get-in db (m/view-config-full-path ctx))
        system-config (get-in db (m/selected-system-config-full-path ctx))
        ls-data (cond-> {:config config}
                  system-config (assoc :system-config system-config))]
    ;; shouldn't contain any sensitive information => safe to save:
    (ls/set-in (ls-path ctx) ls-data)))

(rf/reg-fx
 m/to-local-storage-fx
 to-local-storage)

(defn set-manual-positions
  [{:keys [db] :as cofx} [_ {:keys [value] :as ctx}]]
  (let [new-db (if (nil? value)
                 (-> db
                     (potpuri/dissoc-in (m/manual-positions-full-path ctx)))
                 (-> db
                     (assoc-in (m/manual-positions-full-path ctx)
                               true)))
        reset-system-config? (get-in db (m/selected-system-config-full-path ctx))
        hidden-connectors (when reset-system-config?
                            (resolve-system-config-hidden-connector-ids cofx ctx))
        new-db (if reset-system-config?
                 (-> new-db
                     (potpuri/dissoc-in (m/selected-system-config-full-path ctx))
                     (update-in (m/hide-rules-full-path ctx)
                                assoc :connectors hidden-connectors))
                 new-db)]
    (cond-> {:db new-db
             :fx [[m/to-local-storage-fx {:db new-db
                                          :ctx ctx}]]}
      (nil? value) (assoc m/auto-layout-watch ctx))))

(rf/reg-event-fx
 m/set-manual-positions
 []
 set-manual-positions)

(defn discard-selected-positions
  [{:keys [db] :as cofx} [_ {:keys [map-profiles] :as ctx}]]
  (let [map-profile-ids (->> map-profiles
                             (mapv :db/id))
        reset-system-config? (get-in db (m/selected-system-config-full-path ctx))
        hidden-connectors (when reset-system-config?
                            (resolve-system-config-hidden-connector-ids cofx ctx))
        new-db (-> db
                   (update-in (m/view-positions-full-path ctx)
                              (partial apply dissoc) map-profile-ids)
                   (update-in (m/ephemeral-positions-full-path ctx)
                              (partial apply dissoc) map-profile-ids))
        new-db (if reset-system-config?
                 (-> new-db
                     (potpuri/dissoc-in (m/selected-system-config-full-path ctx))
                     (update-in (m/hide-rules-full-path ctx)
                                assoc :connectors hidden-connectors))
                 new-db)]
    {:db new-db}))

(rf/reg-event-fx
 m/discard-selected-positions
 []
 discard-selected-positions)

(defn view-connector
  [{:keys [db]} [_ {:keys [connector-id] :as ctx}]]
  (let [new-db (-> db
                   (assoc-in (m/selected-edges-full-path ctx)
                             #{connector-id})
                   (assoc-in (m/selected-nodes-full-path ctx)
                             #{}))]
    {:db new-db}))

(rf/reg-event-fx
 m/view-connector
 []
 view-connector)

(defn view-map-profile
  [{:keys [db]} [_ {:keys [map-profile-id] :as ctx}]]
  (let [#_#_new-db (-> db
                       (update-in (m/viewed-full-path ctx)
                                  (constantly map-profile-id)))
        ;;^ epxlicitly viewing - leaving for reference.
        ;; select node - unselect connectors:
        new-db (-> db
                   (assoc-in (m/selected-nodes-full-path ctx)
                             #{map-profile-id})
                   (assoc-in (m/selected-edges-full-path ctx)
                             #{}))]
    {:db new-db}))

(rf/reg-event-fx
 m/view-map-profile
 []
 view-map-profile)

(defn close-viewed-entity
  [{:keys [db]} [_ ctx]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/viewed-full-path ctx))
                   (assoc-in (m/selected-nodes-full-path ctx)
                             #{})
                   (assoc-in (m/selected-edges-full-path ctx)
                             #{}))]
    {:db new-db}))

(rf/reg-event-fx
 m/close-viewed-entity
 []
 close-viewed-entity)

(defn unselect-all
  [{:keys [db]} [_ ctx]]
  (let [new-db (-> db
                   (assoc-in (m/selected-nodes-full-path ctx)
                             #{})
                   (assoc-in (m/selected-edges-full-path ctx)
                             #{}))]
    {:db new-db}))

(rf/reg-event-fx
 m/unselect-all
 []
 unselect-all)

(defn start-delete
  [{:keys [db]} [_ {:keys [connector-id] :as ctx}]]
  (let [connector (-> db
                      (get-in session-model/entities-path)
                      (get connector-id))
        new-db (-> db
                   (assoc-in (m/delete-modal-full-path ctx)
                             {:open? true
                              :connector-id connector-id
                              :connector-type (:graph-reference/type connector)
                              :source-id (:graph-reference/source connector)
                              :target-id (:graph-reference/target connector)}))]
    {:db new-db}))

(rf/reg-event-fx
 m/start-delete
 []
 start-delete)

(defn cancel-delete
  [{:keys [db]} [_ ctx]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/delete-modal-full-path ctx)))]
    {:db new-db}))

(rf/reg-event-fx
 m/cancel-delete
 []
 cancel-delete)

(defn confirm-delete
  [{:keys [db]} [_ {:keys [entity-id] :as ctx}]]
  (let [new-db (-> db
                   (update-in (m/delete-modal-full-path ctx)
                              assoc :deleting? true))]
    {:db new-db
     ::ws/send {:id :entity/disable!
                :body {:entity-id entity-id}
                :on-success [m/delete-success ctx]
                :on-error [m/delete-error ctx]}}))

(rf/reg-event-fx
 m/confirm-delete
 []
 confirm-delete)

(defn delete-success
  [{:keys [db]} [_ {:keys [entity-id] :as ctx} _response]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/delete-modal-full-path ctx))
                   (potpuri/dissoc-in (m/edge-menu-full-path ctx))
                   ;; remove connector-id from selected
                   (update-in (m/selected-edges-full-path ctx)
                              (fnil set/difference #{}) #{entity-id}))]
    {:db new-db}))

(rf/reg-event-fx
 m/delete-success
 []
 delete-success)

(defn delete-error
  [{:keys [db]} [_ ctx error-response]]
  (let [{:keys [error message]} error-response
        msg (cond (string? message) message
                  (keyword? error) (loc/tr error)
                  :else (loc/tr :unknown-error-persists))
        new-db (-> db
                   (potpuri/dissoc-in (m/delete-modal-full-path ctx)))]
    {:db new-db
     :fx [[::modals/open-modal (partial open-error-modal! msg)]]}))

(rf/reg-event-fx
 m/delete-error
 []
 delete-error)

(defn save-view-config
  [{:keys [db]} [_ {:keys [view-config-name
                           main-entity-id] :as ctx}]]
  (let [view-config (get-in db
                            (m/view-config-full-path ctx))
        ;;_ (prn ">save-config" view-config-name view-config)
        entities (-> db
                     (get-in session-model/entities-path)
                     vals)
        main-map-profile (utils/find-main-map-profile
                          {:entities entities
                           :main-entity-id main-entity-id})
        main-map-profile-id (:db/id main-map-profile)
        new-db (-> db
                   (assoc-in (m/saving-view-config-full-path ctx)
                             true))]
    {:db new-db
     ;; later: allow saving existing view-config
     ::ws/send {:id :graph-config/create
                :body {:graph-config/entity main-map-profile-id
                       :graph-config/config view-config
                       :graph-config/name view-config-name}
                :on-success [m/save-view-config-success ctx]
                :on-error [m/save-view-config-error ctx]}}))

(rf/reg-event-fx
 m/save-view-config
 []
 save-view-config)

(defn save-view-config-success
  [{:keys [db]} [_ {:keys [_entity-id] :as ctx} _response]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/saving-view-config-full-path ctx)))]
    {:db new-db}))

(rf/reg-event-fx
 m/save-view-config-success
 []
 save-view-config-success)

(defn save-view-config-error
  [{:keys [db]} [_ ctx error-response]]
  (let [{:keys [error message]} error-response
        msg (cond (string? message) message
                  (keyword? error) (loc/tr error)
                  :else (loc/tr :unknown-error-persists))
        new-db (-> db
                   (potpuri/dissoc-in (m/saving-view-config-full-path ctx)))]
    {:db new-db
     :fx [[::modals/open-modal (partial open-error-modal! msg)]]}))

(rf/reg-event-fx
 m/save-view-config-error
 []
 save-view-config-error)

(defn start-create-connector
  [{:keys [db]} [_ {:keys [map-profile-id connector-type direction] :as ctx}]]
  (let [new-db (-> db
                   (assoc-in (m/create-connector-modal-full-path ctx)
                             {:open? true
                              :map-profile-id map-profile-id
                              :connector-type connector-type
                              :direction direction}))]
    {:db new-db}))

(rf/reg-event-fx
 m/start-create-connector
 []
 start-create-connector)

(defn close-create-connector
  [{:keys [db]} [_ ctx]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/create-connector-modal-full-path ctx))
                   (potpuri/dissoc-in (m/edit-map-profile-full-path ctx)))]
    {:db new-db}))

(rf/reg-event-fx
 m/close-create-connector
 []
 close-create-connector)

(defn update-create-connector
  [{:keys [db]} [_ {:keys [attribute value] :as ctx}]]
  (let [new-db (if (string/blank? value)
                 (-> db
                     (update-in (m/create-connector-edits-full-path ctx)
                                dissoc attribute))
                 (-> db
                     (update-in (m/create-connector-edits-full-path ctx)
                                assoc attribute value)))]
    {:db new-db}))

(rf/reg-event-fx
 m/update-create-connector
 []
 update-create-connector)

(defn start-reconnect
  [{:keys [db]} [_ {:keys [connector-id handle-type] :as ctx}]]
  (let [entities (get-in db session-model/entities-path)
        connector (get entities connector-id)
        node-attr (if (= handle-type "target")
                    :graph-reference/source
                    :graph-reference/target)
        map-profile-id (get connector node-attr)
        ;;map-profile
        new-db (-> db
                   (assoc-in (m/reconnecting-full-path ctx)
                             {:open? true
                              :connector-id connector-id
                              :map-profile-id map-profile-id
                              :direction (if (= handle-type "target")
                                           :source
                                           :target)}))]
    {:db new-db}))

(rf/reg-event-fx
 m/start-reconnect
 []
 start-reconnect)

(defn end-reconnect
  [{:keys [db]} [_ ctx]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/reconnecting-full-path ctx)))]
    {:db new-db}))

(rf/reg-event-fx
 m/end-reconnect
 []
 end-reconnect)

(defn handle-string-to-keyword
  [s]
  (get utils/handle-string-mapping s))

(defn apply-reconnect
  [{:keys [db] :as cofx} [_ {:keys [update] :as ctx}]]
  (let [reconnecting (-> db
                         (get-in (m/reconnecting-full-path ctx)))
        {:keys [connector-id _map-profile-id direction]} reconnecting
        dir-k (if (= direction :target)
                :target-handle
                :source-handle)
        new-handle (get update dir-k)
        handle-key (if (= direction :target)
                     m/target-handle-key
                     m/source-handle-key)
        view-config-update-path [:connectors handle-key connector-id]
        new-val (handle-string-to-keyword new-handle)
        view-config-params {:path view-config-update-path
                            :value new-val}
        view-config-ctx (merge ctx view-config-params)
        ;; done with m/end-reconnect which is also dispatched
        #_#_new-db (-> db
                   (potpuri/dissoc-in (m/reconnecting-full-path ctx)))]
    (update-view-config cofx [m/update-view-config view-config-ctx])))

(rf/reg-event-fx
 m/apply-reconnect
 []
 apply-reconnect)

(rf/reg-fx
 m/fit-view-fx
 (fn [{:keys [provider] :as _ctx}]
   (r/after-render
    (fn []
      ;; first one to prevent unnecessary flashing
      (some-> provider :fit-view .call)
      (r/after-render
       (fn []
         ;; second one to let potential pinned vs unpinned sidebar
         ;; rerender to take place before fit-view:
         (some-> provider :fit-view .call)))))))

;; config as the full configuration data-structure;
;; :named-view/settings
(defn load-view-config
  [{:keys [db]} [_ {:keys [config] :as ctx}]]
  (let [new-db (-> db
                   (assoc-in (m/view-config-full-path ctx)
                             config)
                   (potpuri/dissoc-in (m/selected-system-config-full-path ctx)))
        provider (-> db
                     (get-in (m/provider-full-path ctx)))
        auto-layout-new? (not (get-in new-db (m/manual-positions-full-path ctx)))
        start-watch? auto-layout-new?]
    {:db new-db
     :fx [[m/fit-view-fx (assoc ctx :provider provider)]
          ;; always remove previous watch
          [m/remove-auto-layout-watch ctx]
          (when start-watch?
            [m/auto-layout-watch ctx])
          [m/to-local-storage-fx {:db new-db
                                  :ctx ctx}]]}))

(rf/reg-event-fx
 m/load-view-config
 []
 load-view-config)

(defn open-node-menu
  [{:keys [db]} [_ {:keys [map-profile-id top left] :as ctx}]]
  (let [new-db (-> db
                   (update-in (m/node-menu-full-path ctx)
                              assoc
                              :open? true
                              :map-profile-id map-profile-id
                              :top top
                              :left left)
                   (potpuri/dissoc-in (m/edge-menu-full-path ctx)))]
    {:db new-db}))

(rf/reg-event-fx
 m/open-node-menu
 []
 open-node-menu)

(defn close-node-menu
  [{:keys [db]} [_ ctx]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/node-menu-full-path ctx)))]
    {:db new-db}))

(rf/reg-event-fx
 m/close-node-menu
 []
 close-node-menu)

(defn open-edge-menu
  [{:keys [db]} [_ {:keys [connector-id top left] :as ctx}]]
  (let [new-db (-> db
                   (update-in (m/edge-menu-full-path ctx)
                              assoc
                              :open? true
                              :connector-id connector-id
                              :top top
                              :left left)
                   (potpuri/dissoc-in (m/node-menu-full-path ctx)))]
    {:db new-db}))

(rf/reg-event-fx
 m/open-edge-menu
 []
 open-edge-menu)

(defn close-edge-menu
  [{:keys [db]} [_ ctx]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/edge-menu-full-path ctx)))]
    {:db new-db}))

(rf/reg-event-fx
 m/close-edge-menu
 []
 close-edge-menu)

(defn toggle-show-all-gref-sections
  [{:keys [db]} [_ {:keys [section] :as ctx}]]
  (let [new-db (-> db
                   (update-in (conj (m/show-all-gref-sections-full-path ctx)
                                    section)
                              not))]
    {:db new-db}))

(rf/reg-event-fx
 m/toggle-show-all-gref-sections
 []
 toggle-show-all-gref-sections)

(defn start-selection
  [{:keys [db]} [_ ctx]]
  (let [new-db (-> db
                   (assoc-in (m/selecting-full-path ctx)
                             true))]
    {:db new-db}))

(rf/reg-event-fx
 m/start-selection
 []
 start-selection)

(defn end-selection
  [{:keys [db]} [_ ctx]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/selecting-full-path ctx)))]
    {:db new-db}))

(rf/reg-event-fx
 m/end-selection
 []
 end-selection)

(defn select-system-config
  [{:keys [db]} [_ {:keys [config] :as ctx}]]
  (let [{:keys [db/id]} config
        view-config (get-in m/system-level-configs [id :config])
        new-db (-> db
                   (assoc-in (m/view-config-full-path ctx)
                             view-config)
                   (assoc-in (m/selected-system-config-full-path ctx)
                             id))]
    {:db new-db
     :fx [[m/remove-auto-layout-watch ctx]
          [:dispatch [m/auto-layout-nodes ctx]]
          [m/auto-layout-watch ctx]]}))

(rf/reg-event-fx
 m/select-system-config
 []
 select-system-config)

(defn unselect-system-config
  [{:keys [db]} [_ ctx]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/selected-system-config-full-path ctx)))]
    {:db new-db}))

(rf/reg-event-fx
 m/unselect-system-config
 []
 unselect-system-config)

(rf/reg-cofx
 m/tenant-schema-cofx
 (fn [cofx _]
   (assoc cofx :tenant-schema @session/tenant-schema)))

(rf/reg-cofx
 m/session-entities-cofx
 (fn [cofx _]
   (assoc cofx :session-entities @session/entities)))

(rf/reg-cofx
 m/timestamp-minutes-cofx
 (fn [cofx _]
   (let [now (js/Date.)
         year (.getFullYear now)
         month (inc (.getMonth now))
         day (.getDate now)
         hour (.getHours now)
         minute (.getMinutes now)
         pad-2 (fn [num]
                 (if (< num 10)
                   (str 0 num)
                   (str num)))
         ts (str year (pad-2 month) (pad-2 day)
                 (pad-2 hour) (pad-2 minute))]
     (assoc cofx
            :timestamp-minutes ts))))

(defn export-visible
  [{:keys [_tenant-schema
           _session-entities
           timestamp-minutes
           db] :as cofx} [_ ctx]]
  (let [new-db (-> db
                   #_(assoc-in (m/exporting-full-path ctx) true))
        entities (-> db
                     (get-in session-model/entities-path))
        data (export/get-sheet-data (assoc cofx
                                           :entities entities) ctx)
        ;; TODO later: export graph as image, set it inside the first sheet
        filename (str "entity-graph-" timestamp-minutes)]
    {:db new-db
     :fx [[m/export-as-excel-fx {:filename filename
                                 :sheets-data data}]]}))

(rf/reg-event-fx
 m/export-visible
 [(rf/inject-cofx m/tenant-schema-cofx)
  (rf/inject-cofx m/session-entities-cofx)
  (rf/inject-cofx m/timestamp-minutes-cofx)]
 export-visible)
